// 세리사무소 포털 — 오늘의 업무 (목록 3탭 ┆ 실행 패널).
//
// 기획서 6.4 의 Zero-Navigation 이 이 화면의 전부다: 목록에서 고르면 새 메뉴로 가지 않고
// 오른쪽 패널에서 판단하고, 승인하면 다음 건이 자동으로 열린다. 그래서 목록과 패널이
// 한 상태(useWorkQueue)를 함께 본다 — 브리핑·수익 카드도 같은 상태를 건드려 이 패널을 연다.
//
// 상태를 훅으로 빼 둔 이유: 브리핑 3칸과 수익 기회 카드가 "이 항목을 패널에 띄워라"를
// 요구하는데, 그 셋이 형제 컴포넌트다. 부모(Home.zeirishi)가 훅을 들고 내려주면
// 전역 스토어 없이 셋이 같은 것을 본다.
import React, { useCallback, useMemo, useState } from 'react';
import { navigate } from '../../lib/navigation';
import {
  AUTO_ETC, AUTO_ITEMS, AUTO_TOTAL, OPPORTUNITIES, WORK_ITEMS, companyOf,
} from './Home.zeirishi.data';
import type { AutoItem, WorkItem } from './Home.zeirishi.data';
import styles from './Home.zeirishi.main.module.css';

export type QueueTab = 'todo' | 'done' | 'auto';

/** 업무·기회를 통틀어 패널에 띄울 수 있는 것 — id 로 찾는다 */
const ALL_ITEMS: WorkItem[] = WORK_ITEMS.concat(OPPORTUNITIES);
const findItem = (id: string | null) => (id ? ALL_ITEMS.filter(i => i.id === id)[0] : undefined);
const findAuto = (id: string | null) => (id ? AUTO_ITEMS.filter(a => a.id === id)[0] : undefined);
/** 수익 기회인가 — 패널의 문구와 버튼 색이 갈린다 */
const isOpp = (item: WorkItem) => item.id.charAt(0) === 'o';

/** 승인 시각 — 목업이라 실제 시계가 아니라 09:12 부터 7분씩 민 값을 쓴다 */
function stampAt(n: number): string {
  const m = 9 * 60 + 12 + n * 7;
  const hh = String(Math.floor(m / 60));
  const mm = String(m % 60);
  return `${hh.length < 2 ? '0' + hh : hh}:${mm.length < 2 ? '0' + mm : mm}`;
}

export interface WorkQueue {
  tab: QueueTab;
  setTab: (t: QueueTab) => void;
  selectedId: string | null;
  select: (id: string) => void;
  /** 승인한 업무 id — 배열로 둔다(순서가 곧 처리 순서) */
  doneIds: string[];
  doneAt: { [id: string]: string };
  liveItems: WorkItem[];
  doneItems: WorkItem[];
  approve: () => void;
  undo: (id: string) => void;
  /** 브리핑·수익 카드가 특정 항목을 패널에 띄울 때 — 탭까지 맞춰 준다 */
  focus: (id: string) => void;
}

/** 오늘의 업무 상태 — 부모가 들고 work·brief·revenue 에 나눠 준다 */
export function useWorkQueue(toast: (msg: string) => void): WorkQueue {
  const [tab, setTabState] = useState<QueueTab>('todo');
  const [selectedId, setSelectedId] = useState<string | null>('r1');
  const [doneIds, setDoneIds] = useState<string[]>([]);
  const [doneAt, setDoneAt] = useState<{ [id: string]: string }>({});

  const liveItems = useMemo(
    () => WORK_ITEMS.filter(i => doneIds.indexOf(i.id) < 0),
    [doneIds],
  );

  /* 처리한 순서의 역순 — 방금 승인한 건이 맨 위에 온다 */
  const doneItems = useMemo(
    () => doneIds.map(id => WORK_ITEMS.filter(i => i.id === id)[0]).filter(Boolean).reverse(),
    [doneIds],
  );

  const select = useCallback((id: string) => setSelectedId(id), []);

  const setTab = useCallback((t: QueueTab) => {
    setTabState(t);
    // 탭을 옮기면 그 탭의 첫 건을 연다 — 빈 패널로 떨어지지 않게
    const first = t === 'todo' ? liveItems[0] : t === 'done' ? doneItems[0] : AUTO_ITEMS[0];
    setSelectedId(first ? first.id : null);
  }, [liveItems, doneItems]);

  const approve = useCallback(() => {
    const item = findItem(selectedId);
    if (!item || doneIds.indexOf(item.id) >= 0) {
      return;
    }
    toast(item.okMsg);

    setDoneIds(prev => prev.concat([item.id]));
    setDoneAt(prev => {
      const next = { ...prev };
      next[item.id] = stampAt(Object.keys(prev).length);
      return next;
    });

    if (isOpp(item)) {
      // 수익 기회는 '오늘의 업무' 큐에 속하지 않는다 — 제안을 보냈다고 업무가 줄지 않는다.
      // 고른 것을 그대로 두면 패널이 '발송 완료' 상태로 바뀌어 같은 제안을 두 번 보내지 않는다.
      return;
    }
    // 승인하면 다음 건이 자동으로 열린다 (기획서 6.4 — Enter 한 번으로 이어진다)
    const rest = liveItems.filter(i => i.id !== item.id);
    setSelectedId(rest.length ? rest[0].id : null);
  }, [selectedId, doneIds, liveItems, toast]);

  const undo = useCallback((id: string) => {
    setDoneIds(prev => prev.filter(x => x !== id));
    setDoneAt(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setSelectedId(id);
    // 수익 기회는 큐 밖이라 탭을 옮기면 안 된다 — 되돌릴 자리가 '확인 필요'가 아니다
    if (id.charAt(0) === 'o') {
      toast('제안 발송을 취소했습니다.');
      return;
    }
    setTabState('todo');
    toast('처리를 취소했습니다. 확인 필요 목록으로 되돌렸습니다.');
  }, [toast]);

  const focus = useCallback((id: string) => {
    setTabState(findAuto(id) ? 'auto' : 'todo');
    setSelectedId(id);
  }, []);

  /* 한 덩어리로 묶어 돌려준다 — 매 렌더마다 새 객체를 만들면 이 값을 deps 로 쓰는 쪽
     (키보드 핸들러 등록 등)이 렌더마다 다시 붙는다. */
  return useMemo(
    () => ({ tab, setTab, selectedId, select, doneIds, doneAt, liveItems, doneItems, approve, undo, focus }),
    [tab, setTab, selectedId, select, doneIds, doneAt, liveItems, doneItems, approve, undo, focus],
  );
}

interface Props {
  queue: WorkQueue;
  toast: (msg: string) => void;
  /** 업무를 다 끝냈을 때 '수익 기회 살펴보기' — 큐가 비는 순간이 넘어갈 시점이다 */
  onGoRevenue: () => void;
}

/** 반짝이 아이콘 — AI 가 말하는 자리에만 쓴다 */
function Spark() {
  return (
    <svg viewBox="0 0 24 24" width="11" height="11" fill="#fff">
      <path d="M12 3l1.9 5.6L19.5 10l-5.6 1.9L12 17l-1.9-5.1L4.5 10l5.6-1.4z" />
    </svg>
  );
}

export default function ZeirishiWork({ queue, toast, onGoRevenue }: Props) {
  const { tab, setTab, selectedId, select, doneIds, doneAt, liveItems, doneItems } = queue;

  const total = WORK_ITEMS.length;
  const doneN = total - liveItems.length;

  /* ERP 탈출구 — 기획서 6.4. 회사 컨텍스트를 들고 가는 대리 진입은 권한 토큰이 필요해
     아직 없다. 지금은 회계 모듈로만 보내고, 그 사실을 사용자에게 말해 준다. */
  const openErp = (item: WorkItem) => {
    const c = companyOf(item.co);
    toast(`${c.nm} — 회계 모듈로 이동합니다. 회사 컨텍스트 전달은 대리 진입 설계 후 붙습니다.`);
    navigate('/finance');
  };

  const renderTabs = () => {
    const tabs: [QueueTab, string, number][] = [
      ['todo', '확인 필요', liveItems.length],
      ['done', '진행한 업무', doneItems.length],
      ['auto', 'AI 자동처리', AUTO_TOTAL],
    ];
    return tabs.map(([k, label, n]) => (
      <button
        key={k}
        type="button"
        className={`${styles.qtab} ${tab === k ? styles.qtabOn : ''}`}
        onClick={() => setTab(k)}
      >
        {label}<span className={styles.n5}>{n}</span>
      </button>
    ));
  };

  const renderQueue = () => {
    if (tab === 'auto') {
      return (
        <>
          {AUTO_ITEMS.map(a => (
            <div
              key={a.id}
              className={`${styles.qi} ${a.id === selectedId ? styles.qiOn : ''}`}
              onClick={() => select(a.id)}
            >
              <span className={styles.ck3}>✓</span>
              <div className={styles.qb}>
                <div className={styles.co2}>{a.who} · {a.cat}</div>
                <div className={styles.ti}>{a.title}</div>
              </div>
              <span className={styles.tmx}>{a.tm}</span>
            </div>
          ))}
          <div className={styles.autoEtc}>
            그 외 <b>{AUTO_ETC}건</b> — 전표 자동분개 · 증빙 수집 · 잔액 대사
          </div>
        </>
      );
    }

    if (tab === 'done') {
      if (!doneItems.length) {
        return (
          <div className={styles.empty}>
            <div className={styles.emptyBig}>📋</div>
            아직 진행한 업무가 없습니다.<br /><b>확인 필요</b> 탭에서 승인하면 이곳에 쌓입니다.
          </div>
        );
      }
      return doneItems.map(i => {
        const c = companyOf(i.co);
        return (
          <div
            key={i.id}
            className={`${styles.qi} ${styles.qiDone} ${i.id === selectedId ? styles.qiOn : ''}`}
            onClick={() => select(i.id)}
          >
            <span className={styles.ck3}>✓</span>
            <div className={styles.qb}>
              <div className={styles.co2}>{c.nm} · {i.cat}</div>
              <div className={styles.ti}>{i.title}</div>
            </div>
            <span className={styles.tmx}>{doneAt[i.id] || ''}</span>
          </div>
        );
      });
    }

    if (!liveItems.length) {
      return (
        <div className={styles.empty}>
          <div className={styles.emptyBig}>☕️</div>
          확인할 업무를 모두 끝냈습니다.<br /><b>진행한 업무</b> 탭에서 기록을 확인하세요.
        </div>
      );
    }

    return liveItems.map(i => {
      const c = companyOf(i.co);
      return (
        <div
          key={i.id}
          className={`${styles.qi} ${i.id === selectedId ? styles.qiOn : ''}`}
          onClick={() => select(i.id)}
        >
          <div className={styles.ic2}>{c.ab}</div>
          <div className={styles.qb}>
            <div className={styles.co2}>{c.nm} · {i.cat}</div>
            <div className={styles.ti}>{i.title}</div>
          </div>
          {/* 한 줄의 마지막 칸이 쟁점 — 클릭 전에 머릿속 준비가 되도록 (기획서 6.4) */}
          <span className={styles.issue}>{i.pt}</span>
          <span className={`${styles.bdg} ${styles[i.dueLv]}`}>{i.due}</span>
        </div>
      );
    });
  };

  /* ── 실행 패널 ── */
  const renderAutoPanel = (a: AutoItem) => (
    <>
      <div className={styles.phead}>
        <div className={styles.hrow}>
          <span className={`${styles.pm} ${styles.pmOk}`}>✓</span>
          <span className={styles.pco}>{a.who} · {a.cat}</span>
          <span className={`${styles.pdue} ${styles.dn}`}>{a.tm} 완료</span>
        </div>
        <h2 className={styles.ptitle}>{a.title}</h2>
        <div className={styles.pdone}>
          <span className={styles.ck4}>✓</span>
          <div>
            <div className={styles.lb4}>AI 자동 처리 · 사람 확인 불필요</div>
            <div className={styles.tx3}>{a.md}</div>
          </div>
        </div>
      </div>
      <div className={styles.pbody}>
        <div className={styles.secLine}>처리 로그</div>
        {a.log.map(l => (
          <div key={l[0] + l[1]} className={styles.alr}>
            <span className={styles.at}>{l[0]}</span>
            <span className={styles.an}>{l[1]}</span>
            <span className={styles.am}>{l[2]}</span>
          </div>
        ))}
      </div>
      <div className={styles.pfoot}>
        <span className={styles.kbd}>모든 자동 처리는 감사 로그에 원본과 함께 보존됩니다</span>
        <button type="button" className={styles.btn} onClick={() => toast('감사 로그를 내려받습니다')}>감사 로그</button>
        <button type="button" className={`${styles.btn} ${styles.btnPri}`} onClick={() => toast('처리 결과 원본 화면을 엽니다')}>원본 열기</button>
      </div>
    </>
  );

  const renderDonePanel = (i: WorkItem) => {
    const c = companyOf(i.co);
    return (
      <>
        <div className={styles.phead}>
          <div className={styles.hrow}>
            <span className={styles.pm}>{c.ab}</span>
            <span className={styles.pco}>{c.nm} · {i.cat}</span>
            <span className={`${styles.pdue} ${styles.dn}`}>{doneAt[i.id] || ''} 처리</span>
          </div>
          <h2 className={styles.ptitle}>{i.title}</h2>
          <div className={styles.pdone}>
            <span className={styles.ck4}>✓</span>
            <div>
              <div className={styles.lb4}>처리 완료 · 승인자 기록됨</div>
              <div className={styles.tx3}>{i.okMsg}</div>
            </div>
          </div>
        </div>
        <div className={styles.pbody}>
          <div className={styles.secLine}>
            {i.secN ? <span className={styles.secN}>{i.secN}</span> : null}{i.secT}
          </div>
          <div dangerouslySetInnerHTML={{ __html: i.key }} />
        </div>
        <div className={styles.pfoot}>
          <span className={styles.kbd}>처리 시각 · 승인자 · 근거 자료가 감사 로그에 함께 남습니다</span>
          <button type="button" className={styles.btn} onClick={() => queue.undo(i.id)}>처리 취소</button>
          <button type="button" className={styles.btn} onClick={() => toast('감사 로그를 내려받습니다')}>감사 로그</button>
          <button type="button" className={`${styles.btn} ${styles.btnPri}`} onClick={() => openErp(i)}>{c.ab} ERP에서 열기</button>
        </div>
      </>
    );
  };

  const renderItemPanel = (i: WorkItem) => {
    const c = companyOf(i.co);
    const opp = isOpp(i);
    return (
      <>
        <div className={styles.phead}>
          <div className={styles.hrow}>
            <span className={styles.pm}>{c.ab}</span>
            <span className={styles.pco}>{c.nm} · {i.cat}</span>
            <span className={`${styles.pdue} ${styles[i.dueLv]}`}>{i.due}</span>
          </div>
          <h2 className={styles.ptitle}>{i.title}</h2>
          <div className={styles.pask}>
            <span className={styles.sp2}><Spark /></span>
            <div>
              <div className={styles.lb3}>{opp ? 'AI 매칭 근거' : 'AI가 확인을 요청한 이유'}</div>
              {/* 목업 고정 문자열의 <b> 강조 — 서버가 준 값이 아니다 */}
              <div className={styles.tx2} dangerouslySetInnerHTML={{ __html: i.ask }} />
            </div>
          </div>
        </div>
        <div className={styles.pbody}>
          <div className={styles.secLine}>
            {i.secN ? <span className={styles.secN}>{i.secN}</span> : null}{i.secT}
          </div>
          <div dangerouslySetInnerHTML={{ __html: i.key }} />
          {i.more ? (
            <details className={styles.more}>
              <summary><span className={styles.ch}>▶</span>AI가 자동으로 처리한 내역 보기</summary>
              <div className={styles.mb} dangerouslySetInnerHTML={{ __html: i.more }} />
            </details>
          ) : null}
        </div>
        <div className={styles.pfoot}>
          <span className={styles.kbd}>
            <b>Enter</b> 승인 · 처리하면 다음 건이 자동으로 열립니다
          </span>
          <button type="button" className={styles.btn} onClick={() => toast('보류했습니다. 내일 브리핑에 다시 올라옵니다.')}>보류</button>
          <button type="button" className={styles.btn} onClick={() => openErp(i)}>{c.ab} ERP에서 열기</button>
          <button
            type="button"
            className={`${styles.btn} ${opp ? styles.btnVio : styles.btnPri}`}
            onClick={queue.approve}
          >
            {i.ok}
          </button>
        </div>
      </>
    );
  };

  const renderPanel = () => {
    const auto = findAuto(selectedId);
    if (auto) {
      return renderAutoPanel(auto);
    }
    const item = findItem(selectedId);
    if (item && doneIds.indexOf(item.id) >= 0) {
      return renderDonePanel(item);
    }
    if (item) {
      return renderItemPanel(item);
    }
    // 고른 것이 없다 — 남은 업무가 있으면 안내, 다 끝냈으면 넘어갈 곳을 준다
    if (liveItems.length) {
      return (
        <div className={`${styles.empty} ${styles.emptyMid}`}>
          <div className={styles.emptyBig}>✓</div>
          왼쪽에서 업무를 선택하면<br />이 자리에서 바로 처리할 수 있습니다.
        </div>
      );
    }
    return (
      <div className={styles.alldone}>
        <div className={styles.ck2}>✓</div>
        <div className={styles.t4}>오늘 확인할 업무를 모두 끝냈습니다</div>
        <div className={styles.d4}>
          AI가 처리한 <b>{AUTO_TOTAL + doneIds.length}건</b>과 함께 기록이 남았습니다.<br />
          다음 브리핑은 내일 오전 8시입니다.
        </div>
        {/* 업무 큐가 비는 순간이 돈 버는 일로 넘어갈 시점이다 (기획서 6.4) */}
        <button type="button" onClick={onGoRevenue}>수익 기회 살펴보기 →</button>
      </div>
    );
  };

  return (
    <section className={styles.worklane}>
      <div className={styles.wsec3}>
        <span>오늘의 업무</span>
        <span className={styles.ln} />
        <span className={styles.wnote}>
          <span className={styles.g2} />
          확인 필요 <b>{liveItems.length}건</b> · AI 처리 완료 <b>{AUTO_TOTAL}건</b>
        </span>
        <button type="button" className={styles.wlog} onClick={() => toast('업무 이력은 사이드바 단계에서 붙습니다')}>
          전체 이력 →
        </button>
      </div>

      <div className={styles.lane}>
        <div className={styles.card}>
          <div className={styles.qhead2}>
            <div className={styles.qtabs}>{renderTabs()}</div>
            <span className={styles.qp}>
              {tab === 'todo'
                ? (liveItems.length ? `${Math.min(doneN + 1, total)} / ${total}` : `${total} / ${total} 완료`)
                : ''}
            </span>
            <div className={styles.qbar}>
              <i style={{ width: tab === 'todo' ? `${(doneN / total) * 100}%` : '0%' }} />
            </div>
          </div>
          <div className={styles.queue}>{renderQueue()}</div>
        </div>

        <div className={`${styles.card} ${styles.panel}`}>{renderPanel()}</div>
      </div>
    </section>
  );
}
